import { useEffect, useState } from "react";
import { api, ApiError, type BodyOverview, type WeightRow } from "../../lib/api";
import { fmtKg, fmtTimeShort, fmtPct } from "../../lib/format";
import { EmptyState } from "../../components/EmptyState";
import { BodyStat, Delta } from "./_shared";

export default function WeightOverview() {
  const [ov, setOv] = useState<BodyOverview | null | "error">(null);
  const [recent, setRecent] = useState<WeightRow[] | null>(null);

  // manual entry
  const [weight, setWeight] = useState("");
  const [bodyFat, setBodyFat] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [idemKey, setIdemKey] = useState(() => crypto.randomUUID());

  function load() {
    api.body.overview().then(setOv).catch(() => setOv("error"));
    api.listWeights(15).then((r) => setRecent(r.weights)).catch(() => setRecent([]));
  }
  useEffect(load, []);

  const o = ov && ov !== "error" ? ov : null;
  const latest = o?.latest ?? null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const w = Number(weight);
    if (!Number.isFinite(w) || w <= 0) {
      setErr("Enter a valid weight in kg");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.postWeight({
        weight_kg: w,
        body_fat_pct: bodyFat ? Number(bodyFat) : undefined,
        idempotency_key: idemKey,
      });
      setWeight("");
      setBodyFat("");
      setIdemKey(crypto.randomUUID());
      load();
    } catch (e) {
      setErr(e instanceof ApiError ? `API ${e.status}` : (e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="animate-fade-in">
      {latest == null ? (
        <EmptyState error={ov === "error"} title="No weigh-ins yet">
          Step on the Withings scale (or log one below) and your current weight, trend and body
          composition appear here.
        </EmptyState>
      ) : (
        <>
          {/* Headline current weight + smoothed trend */}
          <div className="card card-pad">
            <div className="flex items-end justify-between">
              <div>
                <div className="eyebrow mb-1">Current</div>
                <div className="font-display text-4xl tabular-nums text-ink">{fmtKg(latest.weight_kg)}</div>
                <div className="mt-1 text-sm text-ink-subtle">
                  7-day trend {o!.avg7 != null ? fmtKg(o!.avg7) : "—"} · {fmtTimeShort(latest.recorded_at)}
                </div>
              </div>
              {o!.bmi != null && (
                <div className="text-right">
                  <div className="font-display text-2xl tabular-nums text-ink">{o!.bmi}</div>
                  <div className="text-[11px] uppercase tracking-wider text-ink-subtle">BMI · {o!.bmi_category}</div>
                </div>
              )}
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2 text-sm">
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-wider text-ink-subtle">Since last</div>
                <div className="mt-0.5"><Delta kg={o!.delta_last} /></div>
              </div>
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-wider text-ink-subtle">7 days</div>
                <div className="mt-0.5"><Delta kg={o!.delta_7d} /></div>
              </div>
              <div className="text-center">
                <div className="text-[10px] uppercase tracking-wider text-ink-subtle">30 days</div>
                <div className="mt-0.5"><Delta kg={o!.delta_30d} /></div>
              </div>
            </div>
          </div>

          {/* Composition snapshot */}
          {(latest.body_fat_pct != null || latest.muscle_mass_kg != null) && (
            <div className="mt-4 grid grid-cols-4 gap-2">
              <BodyStat label="Body fat" value={latest.body_fat_pct != null ? fmtPct(latest.body_fat_pct) : null} unit="%" accent />
              <BodyStat label="Fat mass" value={o!.fat_mass_kg} unit="kg" />
              <BodyStat label="Lean mass" value={o!.lean_mass_kg} unit="kg" />
              <BodyStat label="Muscle" value={latest.muscle_mass_kg} unit="kg" />
            </div>
          )}
        </>
      )}

      {/* Manual entry */}
      <details className="card-pad mt-4">
        <summary className="field-label cursor-pointer">Log a weigh-in manually</summary>
        <form onSubmit={submit} className="mt-3 space-y-4">
          <label className="block">
            <span className="field-label">Weight (kg)</span>
            <input inputMode="decimal" value={weight} onChange={(e) => setWeight(e.target.value)} className="input mt-1.5" placeholder="85.5" />
          </label>
          <label className="block">
            <span className="field-label">Body fat % (optional)</span>
            <input inputMode="decimal" value={bodyFat} onChange={(e) => setBodyFat(e.target.value)} className="input mt-1.5" placeholder="22.1" />
          </label>
          {err && <div className="text-sm text-loss">{err}</div>}
          <button disabled={busy} className="btn-secondary w-full py-3">{busy ? "Saving…" : "Save"}</button>
        </form>
      </details>

      <h2 className="section-title mt-8 mb-3">Recent weigh-ins</h2>
      <div className="card overflow-hidden">
        <table className="data-table">
          <tbody>
            {(recent ?? []).map((w) => (
              <tr key={w.id}>
                <td className="amount-spend font-display text-base">{fmtKg(w.weight_kg)}</td>
                <td className="text-ink-subtle">{w.body_fat_pct != null ? `${fmtPct(w.body_fat_pct)}% fat` : "—"}</td>
                <td className="text-right text-ink-subtle">
                  {fmtTimeShort(w.recorded_at)}
                  <span className="pill-neutral ml-2">{w.source}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
